/**
 * Coordinates multiple app instances in the same browser (same origin): tabs, new windows,
 * and other top-level browsing contexts. A context is "stale" if another opened later
 * (higher openedAt) is still present.
 * Uses BroadcastChannel when available; otherwise the coordinator is a no-op.
 */

const CHANNEL_NAME = 'lens_app_instance_v1';
const TAB_ID_KEY = 'lens_app_tab_id';

function getOrCreateTabId() {
    try {
        let id = sessionStorage.getItem(TAB_ID_KEY);
        if (!id) {
            id =
                typeof crypto !== 'undefined' && crypto.randomUUID
                    ? crypto.randomUUID()
                    : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
            sessionStorage.setItem(TAB_ID_KEY, id);
        }
        return id;
    } catch {
        return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    }
}

function isPeerStale(myTabId, myOpenedAt, peerMap) {
    for (const [id, openedAt] of peerMap) {
        if (id !== myTabId && openedAt > myOpenedAt) {
            return true;
        }
    }
    return false;
}

/**
 * @param {{ onStaleFocus: () => void; onStaleCleared?: () => void }} options
 * @returns {() => void} dispose
 */
export function createMultiTabInstanceCoordinator({ onStaleFocus, onStaleCleared }) {
    if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') {
        return () => {};
    }

    const myTabId = getOrCreateTabId();
    const myOpenedAt = Date.now();
    const peerMap = new Map();

    const bc = new BroadcastChannel(CHANNEL_NAME);

    const recalcStale = () => isPeerStale(myTabId, myOpenedAt, peerMap);

    const broadcastHello = () => {
        try {
            bc.postMessage({ type: 'hello', tabId: myTabId, openedAt: myOpenedAt });
        } catch {
            /* ignore */
        }
    };

    const handleMessage = event => {
        const data = event && event.data;
        if (!data || typeof data !== 'object') {
            return;
        }
        const beforeStale = recalcStale();
        if (data.type === 'hello') {
            const prev = peerMap.get(data.tabId);
            peerMap.set(data.tabId, data.openedAt);
            if (!prev && data.tabId !== myTabId) {
                broadcastHello();
            }
        } else if (data.type === 'bye') {
            peerMap.delete(data.tabId);
        }
        const afterStale = recalcStale();
        if (beforeStale && !afterStale && typeof onStaleCleared === 'function') {
            onStaleCleared();
        }
    };

    bc.onmessage = handleMessage;

    broadcastHello();

    const notifyIfStale = () => {
        if (recalcStale()) {
            onStaleFocus();
        }
    };

    const onVisibility = () => {
        if (document.visibilityState === 'visible') {
            notifyIfStale();
        }
    };

    document.addEventListener('visibilitychange', onVisibility);

    // Switching between two windows can leave both documents "visible" for Page Visibility;
    // focus catches returning to an older window in that case.
    window.addEventListener('focus', notifyIfStale);

    const onPageHide = () => {
        try {
            bc.postMessage({ type: 'bye', tabId: myTabId });
        } catch {
            /* ignore */
        }
    };

    window.addEventListener('pagehide', onPageHide);

    return () => {
        try {
            bc.postMessage({ type: 'bye', tabId: myTabId });
        } catch {
            /* ignore */
        }
        document.removeEventListener('visibilitychange', onVisibility);
        window.removeEventListener('focus', notifyIfStale);
        window.removeEventListener('pagehide', onPageHide);
        bc.onmessage = null;
        bc.close();
    };
}
